import type {
    ProductAttributes,
    ProductVariationAttributes,
    IngredientAttributes
} from '~/types';

export const useProductStore = defineStore('products', () => {
    const api = useApi();
    const products = ref<ProductAttributes[]>([]);
    const productId = ref<ProductAttributes['id'] | null>(null);
    const productsLoading = ref(false);
    const productsLoaded = ref(false);

    /**
     * The currently viewed product.
     */
    const product = computed<ProductAttributes | null>(() => {
        return products.value.find(p => p.id === Number(productId.value)) ?? null;
    });

    /**
     * @deprecated This store will be going away in the future.
     * @see {getAllProducts}
     * @param force
     */
    const loadProducts = async (force = false) => {
        if (productsLoading.value) {
        // wait for an existing load to finish
            return new Promise(resolve => {
                const stopWatch = watch(() => productsLoading.value, loading => {
                    if (!loading) {
                        resolve(void 0);
                        stopWatch();
                    }
                });
            });
        }

        if (productsLoaded.value && !force) {
            return;
        }

        productsLoading.value = true;
        products.value = await getAllProducts(api).finally(() => productsLoading.value = false);
        productsLoaded.value = true;
    };

    /**
     * Load product attributes that are only available when logged in.
     *
     * @param id
     * todo - move this out from the store
     */
    const ensureFullyLoaded = async (id: ProductAttributes['id'] | ProductAttributes['slug']) => {
        // if no products are loaded, load them
        if (!productsLoaded.value) {
            await loadProducts();
        }

        // if products are still loading, wait for them to finish
        if (productsLoading.value) {
            await new Promise(resolve => {
                const interval = setInterval(() => {
                    if (!productsLoading.value) {
                        clearInterval(interval);
                        resolve(void 0);
                    }
                }, 100);
            });
        }

        const idIsSlug = isNaN(Number(id));
        const product = products.value.find(p => (idIsSlug ? p.slug : p.id) === id);

        if (!product) {
            throw createError({
                statusCode: 404,
                statusMessage: 'Not Found',
                message: 'Product Not Found.',
                fatal: false
            });
        }

        const fullyLoaded = ['preparation_time', 'instructions', 'in_draft']
            .every(attribute => product[attribute] !== undefined);

        if (!fullyLoaded) {
            const data = await api<ProductAttributes>(`products/${id}`);
            products.value = products.value.map(p => {
                if (p.id !== id) {
                    return p;
                }

                data.variations = data.variations.map(
                    (variation: ProductVariationAttributes & { ingredients?: IngredientAttributes[] }) => {
                        if (!variation.ingredients) {
                            return variation;
                        }

                        // if returning ingredients, sort it into the correct shape
                        variation.ingredientsAmounts = variation.ingredients.map(i => ({
                            amount: i.ingredientProductAmount!,
                            ingredientId: i.id
                        }));

                        return variation;
                    });

                return data;
            });
        }
    };

    return {
        products,
        productId,
        productsLoading,
        productsLoaded,
        product,
        loadProducts,
        ensureFullyLoaded
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useProductStore, import.meta.hot));
}
